/**
 * Slide deck workspace (`/slides`) — the editing UI over the Presentation model.
 *
 * File-centric MVP mirroring the sheet editor: open a `.pptx`/`.odp` (parsed
 * server-side by `office-formats`), edit the deck — title + body lines per
 * slide, add/remove/reorder — with a live preview, and export back. The model
 * is deliberately the round-tripped subset (no images/shapes/layouts yet).
 */
import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  onCleanup,
  Show,
} from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  FileUp,
  Loader2,
  Play,
  Plus,
  Presentation as PresentationIcon,
  Trash2,
  X,
} from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { exportOdp, exportPptx, importOdp, importPptx, type Presentation } from "../lib/office-io";
import {
  addSlide,
  emptyPresentation,
  moveSlide,
  removeSlide,
  setSlideBodyText,
  setSlideTitle,
  slideBodyText,
  stepSlideIndex,
} from "../lib/presentation-ops";
import { downloadBlob, stripExtension } from "../lib/download";

type DeckFormat = "pptx" | "odp";

const IMPORTERS: Record<DeckFormat, (file: Blob) => Promise<Presentation>> = {
  pptx: importPptx,
  odp: importOdp,
};
const EXPORTERS: Record<DeckFormat, (deck: Presentation) => Promise<Blob>> = {
  pptx: exportPptx,
  odp: exportOdp,
};

function isDeckFormat(ext: string): ext is DeckFormat {
  return Object.hasOwn(IMPORTERS, ext);
}

interface SlidesEditorProps {
  /** Controlled deck (persisted mode). Omit for the standalone in-memory editor. */
  deck?: Accessor<Presentation>;
  /** Called with the next deck on every edit (persisted mode → autosave). */
  onDeckChange?: (next: Presentation) => void;
  /** Controlled file/document name (persisted mode → page title). */
  fileName?: Accessor<string>;
  onFileNameChange?: (name: string) => void;
  /** Toolbar slot for the save indicator + version-history button. */
  headerExtras?: JSX.Element;
}

export function SlidesEditor(props: SlidesEditorProps = {}) {
  const { t } = useI18n();
  const [internalDeck, setInternalDeck] = createSignal<Presentation>(emptyPresentation());
  const deck = (): Presentation => props.deck?.() ?? internalDeck();
  const setDeck = (updater: Presentation | ((prev: Presentation) => Presentation)): void => {
    const next = typeof updater === "function" ? updater(deck()) : updater;
    if (props.onDeckChange) {
      props.onDeckChange(next);
    } else {
      setInternalDeck(next);
    }
  };
  const [activeIdx, setActiveIdx] = createSignal(0);
  const [busy, setBusy] = createSignal<false | "import" | "export">(false);
  const [internalFileName, setInternalFileName] = createSignal("presentation");
  const fileName = (): string => props.fileName?.() ?? internalFileName();
  const setFileName = (name: string): void => {
    if (props.onFileNameChange) {
      props.onFileNameChange(name);
    } else {
      setInternalFileName(name);
    }
  };
  let fileInput: HTMLInputElement | undefined;

  const active = createMemo(() => deck().slides[activeIdx()]);

  const clampActive = (slides: number) => {
    setActiveIdx((i) => Math.min(i, Math.max(0, slides - 1)));
  };

  // ── Presentation mode ────────────────────────────────────────
  const [presenting, setPresenting] = createSignal<number | null>(null);
  const presentedSlide = createMemo(() => {
    const i = presenting();
    return i === null ? undefined : deck().slides[i];
  });

  const stepPresentation = (delta: number) => {
    setPresenting((i) => (i === null ? null : stepSlideIndex(i, delta, deck().slides.length)));
  };

  const exitPresentation = () => {
    const i = presenting();
    setPresenting(null);
    if (i !== null) {
      // Land the editor on the slide the presentation stopped at.
      clampActive(deck().slides.length);
      setActiveIdx(Math.min(i, Math.max(0, deck().slides.length - 1)));
    }
  };

  createEffect(() => {
    if (presenting() === null) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape": {
          exitPresentation();
          break;
        }
        case "ArrowRight":
        case "ArrowDown":
        case "PageDown":
        case " ": {
          stepPresentation(1);
          break;
        }
        case "ArrowLeft":
        case "ArrowUp":
        case "PageUp": {
          stepPresentation(-1);
          break;
        }
        case "Home": {
          setPresenting(0);
          break;
        }
        case "End": {
          setPresenting(Math.max(0, deck().slides.length - 1));
          break;
        }
        default: {
          return;
        }
      }
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => window.removeEventListener("keydown", onKey));
  });

  const handleImportFile = async (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const importer = isDeckFormat(ext) ? IMPORTERS[ext] : undefined;
    if (!importer) {
      showToast(t(msg`Unsupported presentation file`), "error");
      return;
    }
    setBusy("import");
    try {
      const imported = await importer(file);
      setDeck(imported.slides.length > 0 ? imported : emptyPresentation());
      setActiveIdx(0);
      setFileName(stripExtension(file.name));
    } catch (err) {
      console.error("[slides] import failed:", err);
      showToast(t(msg`Could not import that file`), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async (format: DeckFormat) => {
    if (busy()) {
      return;
    }
    setBusy("export");
    try {
      const blob = await EXPORTERS[format](deck());
      downloadBlob(blob, `${fileName().trim() || "presentation"}.${format}`);
    } catch (err) {
      console.error("[slides] export failed:", err);
      showToast(t(msg`Export failed`), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="flex h-full min-h-0 flex-col gap-3 p-4" data-testid="slides-editor">
      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div class="flex flex-wrap items-center gap-2">
        <div class="mr-auto flex items-center gap-2">
          <PresentationIcon class="h-5 w-5 text-primary shrink-0" />
          <input
            type="text"
            value={fileName()}
            onChange={(e) => setFileName(e.currentTarget.value)}
            data-testid="slides-name-input"
            aria-label={t(msg`File name`)}
            class="h-9 w-44 rounded-md border border-input bg-background px-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        {props.headerExtras}
        <Button
          variant="ghost"
          size="sm"
          class="gap-1.5"
          data-testid="slides-present"
          onClick={() => setPresenting(activeIdx())}
        >
          <Play class="h-4 w-4" />
          {t(msg`Present`)}
        </Button>
        <input
          ref={(el) => (fileInput = el)}
          type="file"
          accept=".pptx,.odp"
          class="hidden"
          data-testid="slides-open-input"
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            if (file) {
              void handleImportFile(file);
            }
            e.currentTarget.value = "";
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          class="gap-1.5"
          data-testid="slides-open"
          disabled={busy() !== false}
          onClick={() => fileInput?.click()}
        >
          <Show when={busy() === "import"} fallback={<FileUp class="h-4 w-4" />}>
            <Loader2 class="h-4 w-4 animate-spin" />
          </Show>
          {t(msg`Open`)}
        </Button>
        <For each={["pptx", "odp"] as const}>
          {(format) => (
            <Button
              variant="ghost"
              size="sm"
              class="gap-1.5"
              data-testid={`slides-export-${format}`}
              disabled={busy() !== false}
              onClick={() => void handleExport(format)}
            >
              <Download class="h-4 w-4" />
              <span class="text-xs font-semibold uppercase">{format}</span>
            </Button>
          )}
        </For>
      </div>

      <div class="flex min-h-0 flex-1 gap-4">
        {/* ── Slide rail ──────────────────────────────────────── */}
        <div
          class="flex w-44 shrink-0 flex-col gap-2 overflow-y-auto pr-1"
          data-testid="slides-rail"
        >
          <For each={deck().slides}>
            {(slide, i) => (
              <button
                type="button"
                data-testid={`slide-thumb-${i()}`}
                aria-current={activeIdx() === i()}
                class="rounded-lg border p-2 text-left transition-colors"
                classList={{
                  "border-primary bg-primary/5 ring-1 ring-primary": activeIdx() === i(),
                  "border-border/50 bg-card/40 hover:bg-secondary/30": activeIdx() !== i(),
                }}
                onClick={() => setActiveIdx(i())}
              >
                <div class="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {i() + 1}
                </div>
                <div class="truncate text-xs font-semibold">
                  {slide.title?.trim() || t(msg`Untitled slide`)}
                </div>
                <div class="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
                  {slide.body.join(" ")}
                </div>
              </button>
            )}
          </For>
          <Button
            variant="ghost"
            size="sm"
            class="gap-1.5"
            data-testid="slide-add"
            onClick={() => {
              const at = activeIdx();
              setDeck((d) => addSlide(d, at));
              setActiveIdx(at + 1);
            }}
          >
            <Plus class="h-4 w-4" />
            {t(msg`Add slide`)}
          </Button>
        </div>

        {/* ── Active slide: preview + edit ─────────────────────── */}
        <div class="flex min-w-0 flex-1 flex-col gap-3">
          {/* Preview surface (16:9-ish, theme-aware). */}
          <div
            class="flex aspect-video w-full max-w-3xl flex-col justify-center gap-4 self-center rounded-xl border border-border/50 bg-card/60 px-10 py-8 shadow-inner"
            data-testid="slide-preview"
          >
            <h2 class="text-2xl font-extrabold tracking-tight" data-testid="slide-preview-title">
              {active()?.title?.trim() || t(msg`Untitled slide`)}
            </h2>
            <ul class="flex flex-col gap-1.5 text-base text-foreground/90">
              <For each={active()?.body ?? []}>
                {(line) => (
                  <li class="flex gap-2">
                    <Show when={line.trim() !== ""}>
                      <span class="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
                    </Show>
                    <span class="min-w-0 break-words">{line}</span>
                  </li>
                )}
              </For>
            </ul>
          </div>

          {/* Editors. */}
          <div class="flex min-h-0 flex-1 flex-col gap-2">
            <input
              type="text"
              value={active()?.title ?? ""}
              data-testid="slide-title-input"
              aria-label={t(msg`Slide title`)}
              placeholder={t(msg`Slide title`)}
              class="h-10 rounded-md border border-input bg-background px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onInput={(e) => {
                const idx = activeIdx();
                setDeck((d) => setSlideTitle(d, idx, e.currentTarget.value));
              }}
            />
            <textarea
              value={slideBodyText(active())}
              data-testid="slide-body-input"
              aria-label={t(msg`Slide content (one bullet per line)`)}
              placeholder={t(msg`One bullet point per line`)}
              class="min-h-24 flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onInput={(e) => {
                const idx = activeIdx();
                setDeck((d) => setSlideBodyText(d, idx, e.currentTarget.value));
              }}
            />
            <div class="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                class="h-8 w-8 p-0"
                data-testid="slide-move-up"
                aria-label={t(msg`Move slide up`)}
                disabled={activeIdx() === 0}
                onClick={() => {
                  const idx = activeIdx();
                  setDeck((d) => moveSlide(d, idx, -1));
                  setActiveIdx(idx - 1);
                }}
              >
                <ChevronUp class="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                class="h-8 w-8 p-0"
                data-testid="slide-move-down"
                aria-label={t(msg`Move slide down`)}
                disabled={activeIdx() >= deck().slides.length - 1}
                onClick={() => {
                  const idx = activeIdx();
                  setDeck((d) => moveSlide(d, idx, 1));
                  setActiveIdx(idx + 1);
                }}
              >
                <ChevronDown class="h-4 w-4" />
              </Button>
              <span
                class="ml-auto text-xs tabular-nums text-muted-foreground"
                data-testid="slide-counter"
              >
                {activeIdx() + 1} / {deck().slides.length}
              </span>
              <Button
                variant="ghost"
                size="sm"
                class="h-8 gap-1.5 text-destructive hover:text-destructive"
                data-testid="slide-delete"
                onClick={() => {
                  const idx = activeIdx();
                  setDeck((d) => {
                    const next = removeSlide(d, idx);
                    clampActive(next.slides.length);
                    return next;
                  });
                }}
              >
                <Trash2 class="h-4 w-4" />
                {t(msg`Delete slide`)}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Presentation mode (full-viewport overlay) ────────────── */}
      <Show when={presenting() !== null}>
        <div
          class="fixed inset-0 z-50 flex flex-col bg-background"
          data-testid="slides-present-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t(msg`Presentation`)}
          onClick={() => stepPresentation(1)}
        >
          <div class="flex min-h-0 flex-1 flex-col justify-center gap-8 px-[8vw]">
            <h2
              class="text-[clamp(1.8rem,6vw,4rem)] font-extrabold leading-tight tracking-tight"
              data-testid="present-title"
            >
              {presentedSlide()?.title?.trim() || t(msg`Untitled slide`)}
            </h2>
            <ul class="flex flex-col gap-3 text-[clamp(1rem,3vw,1.8rem)] text-foreground/90">
              <For each={presentedSlide()?.body ?? []}>
                {(line) => (
                  <li class="flex gap-3">
                    <Show when={line.trim() !== ""}>
                      <span class="mt-[0.8em] h-2 w-2 shrink-0 rounded-full bg-primary/70" />
                    </Show>
                    <span class="min-w-0 break-words">{line}</span>
                  </li>
                )}
              </For>
            </ul>
          </div>
          <div
            class="flex items-center gap-2 border-t border-border/40 px-4 py-3"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="sm"
              class="h-8 w-8 p-0"
              data-testid="present-prev"
              aria-label={t(msg`Previous slide`)}
              disabled={(presenting() ?? 0) <= 0}
              onClick={() => stepPresentation(-1)}
            >
              <ChevronLeft class="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              class="h-8 w-8 p-0"
              data-testid="present-next"
              aria-label={t(msg`Next slide`)}
              disabled={(presenting() ?? 0) >= deck().slides.length - 1}
              onClick={() => stepPresentation(1)}
            >
              <ChevronRight class="h-4 w-4" />
            </Button>
            <span
              class="ml-auto text-xs tabular-nums text-muted-foreground"
              data-testid="present-counter"
            >
              {(presenting() ?? 0) + 1} / {deck().slides.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              class="h-8 gap-1.5"
              data-testid="present-exit"
              onClick={exitPresentation}
            >
              <X class="h-4 w-4" />
              {t(msg`Exit`)}
            </Button>
          </div>
        </div>
      </Show>
    </div>
  );
}
